import { useQuery } from "@apollo/client";
import { useState, useMemo, ReactElement } from "react";
import {
  useQueryParam,
  NumberParam,
  StringParam,
  withDefault,
} from "use-query-params";

import {
  ArchiveSubaccountMutation,
  ArchiveSubaccountMutationVariables,
  CreateSubaccountMutation,
  CreateSubaccountMutationVariables,
  ListSubaccountsForStaffQuery,
  ListSubaccountsQuery,
  SetSubaccountIsWeTextMutation,
  SetSubaccountIsWeTextMutationVariables,
  UnarchiveSubaccountMutation,
  UnarchiveSubaccountMutationVariables,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import Loading from "~frontend/components/Loading";
import { PageHeader } from "~frontend/components/PageHeader";
import { TableBox, TableControls } from "~frontend/components/Table";
import {
  LIST_SUBACCOUNT_OWNER_FIELDS,
  LIST_SUBACCOUNTS_FOR_STAFF,
  CREATE_SUBACCOUNT,
  ARCHIVE_SUBACCOUNT,
  UNARCHIVE_SUBACCOUNT,
  SET_SUBACCOUNT_IS_WE_TEXT,
  GET_SUBACCOUNT_SUPERVOLUNTEER_FIELDS,
  GET_SUBACCOUNT_ADMIN_FIELDS,
} from "~frontend/graphql/subaccounts";
import {
  useCurrentUserIsStaff,
  useLoginInfoRefetch,
} from "~frontend/providers/LoggedInUserProvider";
import useMutation from "~frontend/util/useMutation";

import AddSubaccountDialog from "./AddSubaccountDialog";
import SubaccountTable from "./OrgSubaccountsTable";
import { SubaccountSort, SUBACCOUNT_SORTS } from "./constants";

function validateSort(sort: string): SubaccountSort {
  return (
    SUBACCOUNT_SORTS.find(s => s.key === sort)?.key ?? SUBACCOUNT_SORTS[0].key
  );
}

export default function Subaccounts(): ReactElement {
  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));
  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );
  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, SUBACCOUNT_SORTS[0].key)
  );

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );

  const [weTextFilter, setWeTextFilter] = useQueryParam(
    "we_text",
    withDefault(StringParam, "all")
  );

  const validatedSort = validateSort(sort);

  const isStaff = useCurrentUserIsStaff();
  const refetchLoggedInInfo = useLoginInfoRefetch();

  const { loading, error, data } = useQuery<
    ListSubaccountsQuery | ListSubaccountsForStaffQuery
  >(isStaff ? LIST_SUBACCOUNTS_FOR_STAFF : LIST_SUBACCOUNT_OWNER_FIELDS);

  const [setIsWeText] = useMutation<
    SetSubaccountIsWeTextMutation,
    SetSubaccountIsWeTextMutationVariables
  >(SET_SUBACCOUNT_IS_WE_TEXT, {
    onErrorSnackbarMessage: "Error updating WeText status",
  });

  async function handleSetIsWeText(
    subaccountId: string,
    isWeText: boolean
  ): Promise<void> {
    await setIsWeText({
      variables: { input: { subaccountId, isWeText } },
      refetchQueries: [
        LIST_SUBACCOUNTS_FOR_STAFF,
        {
          query: GET_SUBACCOUNT_SUPERVOLUNTEER_FIELDS,
          variables: { input: { subaccountId } },
        },
        {
          query: GET_SUBACCOUNT_ADMIN_FIELDS,
          variables: { input: { subaccountId } },
        },
      ],
      awaitRefetchQueries: true,
    });
  }

  const [addSubaccountDialogOpen, toggleAddSubaccountDialog] = useState(false);

  const [addSubaccount] = useMutation<
    CreateSubaccountMutation,
    CreateSubaccountMutationVariables
  >(CREATE_SUBACCOUNT, {
    refetchQueries: [LIST_SUBACCOUNT_OWNER_FIELDS],
    awaitRefetchQueries: true,
    onCompleted: () => {
      refetchLoggedInInfo().catch(console.error);
    },
  });

  const [archiveSubaccount] = useMutation<
    ArchiveSubaccountMutation,
    ArchiveSubaccountMutationVariables
  >(ARCHIVE_SUBACCOUNT, {
    refetchQueries: [LIST_SUBACCOUNT_OWNER_FIELDS],
  });

  const [unarchiveSubaccount] = useMutation<
    UnarchiveSubaccountMutation,
    UnarchiveSubaccountMutationVariables
  >(UNARCHIVE_SUBACCOUNT, {
    refetchQueries: [LIST_SUBACCOUNT_OWNER_FIELDS],
  });

  async function handleArchiveSubaccount(
    subaccountId: string,
    archive: boolean
  ) {
    const gqlVars = { variables: { input: { subaccountId } } };
    const { errors } = archive
      ? await archiveSubaccount(gqlVars)
      : await unarchiveSubaccount(gqlVars);
    if (errors) {
      throw errors;
    }
  }

  const filteredSubaccounts = useMemo(() => {
    const allSubaccounts = data?.listSubaccounts ?? [];

    let filteredSubaccounts = isStaff
      ? allSubaccounts
      : allSubaccounts.filter(s => s.archivedAt === null);

    // filter by search
    filteredSubaccounts = search
      ? filteredSubaccounts.filter(s =>
          s.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
        )
      : filteredSubaccounts.slice(0);

    // filter by weText (staff only)
    if (isStaff && weTextFilter !== "all") {
      const isWeText = weTextFilter === "true";
      filteredSubaccounts = filteredSubaccounts.filter(s => {
        const staffRow =
          s as ListSubaccountsForStaffQuery["listSubaccounts"][0];
        return staffRow.isWeText === isWeText;
      });
    }

    // sort
    return filteredSubaccounts.sort((a, b) => {
      switch (validatedSort) {
        case "created_at_asc":
          return a.createdAt - b.createdAt;
        case "created_at_desc":
          return b.createdAt - a.createdAt;
        case "name_asc":
          return (a.name || "").localeCompare(b.name || "");
        case "name_desc":
          return (b.name || "").localeCompare(a.name || "");
      }
    });
  }, [data, validatedSort, search, isStaff, weTextFilter]);

  const rows = filteredSubaccounts.slice(
    page * pageSize,
    (page + 1) * pageSize
  );

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  async function handleAddSubaccount(
    values: CreateSubaccountMutationVariables
  ): Promise<void> {
    const { errors } = await addSubaccount({
      variables: values,
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddSubaccountDialog(false);
    }
  }

  return (
    <>
      <PageHeader>Subaccounts</PageHeader>
      <TableControls
        addButtons={[
          {
            label: "Add Subaccount",
            onClick: () => toggleAddSubaccountDialog(true),
            id: "add-subaccount",
          },
        ]}
        filters={
          isStaff
            ? [
                {
                  id: "we-text",
                  label: "WeText",
                  type: "dropdown" as const,
                  value: weTextFilter,
                  onChange: (value: string) => {
                    setWeTextFilter(value);
                    setPage(0);
                  },
                  options: [
                    { value: "all", label: "All" },
                    { value: "true", label: "WeText" },
                    { value: "false", label: "Not WeText" },
                  ],
                },
              ]
            : undefined
        }
        sort={{
          value: sort,
          onChange: value => setSort(value as SubaccountSort),
          options: SUBACCOUNT_SORTS.map(sort => ({
            value: sort.key,
            label: sort.label,
          })),
        }}
        search={{
          value: search,
          onChange: value => {
            setSearch(value as string);
            setPage(0);
          },
          placeholder: "Search for a subaccount...",
        }}
      />
      <TableBox sx={{ pb: 1 }}>
        {loading ? (
          <Loading />
        ) : (
          <SubaccountTable
            rows={rows}
            handleChangePage={setPage}
            handleChangeRowsPerPage={newRowsPerPage => {
              setPageSize(newRowsPerPage);
              setPage(0);
            }}
            rowsPerPage={pageSize}
            page={page}
            totalCount={filteredSubaccounts.length}
            toggleArchiveSubaccount={handleArchiveSubaccount}
            setIsWeText={isStaff ? handleSetIsWeText : undefined}
          />
        )}
      </TableBox>
      <AddSubaccountDialog
        open={addSubaccountDialogOpen}
        onClose={() => toggleAddSubaccountDialog(false)}
        onSubmit={handleAddSubaccount}
      />
    </>
  );
}
